/**
 * kullaniciDilService.js — the interface language belongs to the USER (#562, 2026-10-30; backend #345).
 *
 * Base: {JAVA_BACKEND_API_URL}/users/me/language (UserController, the caller only):
 *   GET → { languageId: 1|2|null, effectiveLanguageId: 1|2, chosen }  languageId = what the user saved (null = never chose);
 *         effectiveLanguageId = what is in force for them (the choice, else English for a foreign user or a user of a foreign
 *         company, else Turkish) — that derived default is what #535 used to compute in the browser.
 *   PUT { languageId: 1|2|null } → the GET's shape.
 *
 * The browser still keeps the last language in localStorage `appLanguage` (the login and public pages need one with no session);
 * the server's answer is applied at login and at app start, and the header switch saves its choice here.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { LANGUAGE_IDS } from '../i18n';

const URL = `${JAVA_BACKEND_API_URL}/users/me/language`;

/** { languageId, effectiveLanguageId, chosen } for the logged-in user. */
export const getMyLanguage = () => axios.get(URL, { headers: getAuthHeader() }).then((r) => r.data);

/** Saves the user's choice (1 TR / 2 EN, null = not chosen). Same shape as the GET. */
export const setMyLanguage = (languageId) =>
  axios.put(URL, { languageId }, { headers: getAuthHeader() }).then((r) => r.data);

/** "tr" or "en" for a backend language id; anything else is Turkish, like the rest of the app. */
export const languageOfId = (languageId) => (Number(languageId) === LANGUAGE_IDS.en ? 'en' : 'tr');

/**
 * Switches the UI to the logged-in user's effective language. A failed read (backend down, expired session) leaves the browser's
 * language as it is: the language must never block a login. Resolves to the language applied, or null.
 */
export async function applyMyLanguage(setSelectedLang) {
  try {
    const { effectiveLanguageId } = await getMyLanguage();
    const lang = languageOfId(effectiveLanguageId);
    setSelectedLang(lang);
    return lang;
  } catch (e) {
    return null;
  }
}

/**
 * The header switch's choice goes to the server without being waited for (the UI has already switched). No session (login and public
 * pages): nothing to save. A failure is only logged: the browser keeps the choice, the next switch saves again.
 */
export function saveMyLanguageChoice(lang) {
  let loggedIn = false;
  try { loggedIn = Boolean(localStorage.getItem('token')); } catch (e) { /* storage closed */ }
  if (!loggedIn) return Promise.resolve(null);
  return setMyLanguage(LANGUAGE_IDS[lang] ?? LANGUAGE_IDS.tr).catch((e) => {
    console.warn('Language choice was not saved:', e?.message);
    return null;
  });
}
